import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneIcon from "@mui/icons-material/Phone";
import InsightsIcon from "@mui/icons-material/Insights";
import axios from "axios";
import moment from "moment";
import Links from "../../Data/Links";
import { MONTHS, YEARS, PIE_COLORS } from "../../constants";
import getFiscals from "../../utils/getFiscals";
import BarChart from "../../component/Charts/BarChart";
import { Chart as ChartJS } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(ChartDataLabels);

export default function DivisionLevelMonthlyReport() {
  const currentYear = moment().format("YYYY");
  const currentMonth = moment().format("MMM");

  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState([]);
  const [vdpRows, setVdpRows] = useState([]);
  const [acquisitionRows, setAcquisitionRows] = useState([]);
  const [adsRows, setAdsRows] = useState([]);
  const [leadsByDivision, setLeadsByDivision] = useState({
    Auto: { fup: { actual: 0, target: 0 }, internet: { actual: 0, target: 0 }, opportunities: { actual: 0, target: 0 } },
    Used: { fup: { actual: 0, target: 0 }, internet: { actual: 0, target: 0 }, opportunities: { actual: 0, target: 0 } },
    Rec: { fup: { actual: 0, target: 0 }, internet: { actual: 0, target: 0 }, opportunities: { actual: 0, target: 0 } }
  });
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const usedDivisions = ["DriveNation", "FFUN Cars"];

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString();
  };

  const formatSeconds = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString();
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `${Math.round(num * 100)}%`;
  };

  const calcPercent = (actual, target) => {
    if (!target || Number(target) === 0) return "0%";
    return `${Math.round((Number(actual) / Number(target)) * 100)}%`;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const parseNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const getMetricValue = (row, keys, defaultValue = undefined) => {
    if (!row) return defaultValue;
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
        return row[key];
      }
    }
    return defaultValue;
  };

  const getAverageMetric = (items, keys) => {
    if (!Array.isArray(items) || items.length === 0) return undefined;
    const values = items
      .map((item) => Number(getMetricValue(item, keys)))
      .filter((val) => Number.isFinite(val));
    if (values.length === 0) return undefined;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  };

  const getAdsMetric = (row, keys) => {
    return parseNum(getMetricValue(row, keys, 0));
  };

  const getMetric = (row, actualKey, targetKey) => {
    const lowerRow = Object.keys(row || {}).reduce((acc, key) => {
      acc[key.toLowerCase()] = row[key];
      return acc;
    }, {});

    const tryKeys = (obj, keys) => {
      for (const k of keys) {
        const val = obj?.[k] ?? obj?.[k.toLowerCase()];
        if (val !== undefined && val !== null && val !== "") return val;
      }
      return 0;
    };

    const actual = tryKeys(lowerRow, [
      actualKey,
      `${actualKey}_actual`, `${actualKey}actual`, `${actualKey}_act`,
      "fup_actual", "follow_up_actual", "followup_actual", "phone_up_actual", "up_actual",
      "internet_actual", "internet_leads_actual", "int_actual",
      "opportunities_actual", "opps_actual", "opps_act",
      "actual", "value", "actuals"
    ]);
    const target = tryKeys(lowerRow, [
      targetKey,
      `${targetKey}_target`, `${targetKey}target`, `${targetKey}_tgt`,
      "fup_target", "follow_up_target", "followup_target", "phone_up_target", "up_target",
      "internet_target", "internet_leads_target", "int_target",
      "opportunities_target", "opps_target", "opps_tgt",
      "target"
    ]);

    return {
      actual: parseNum(actual || 0),
      target: parseNum(target || 0)
    };
  };

  const getStoreName = (row) => {
    return (
      row?.STORE_NAME ||
      row?.store_name ||
      row?.store?.store_name ||
      row?.store?.name ||
      row?.store ||
      row?.dealership ||
      row?.name ||
      row?.storeName ||
      row?.store_id ||
      row?.id ||
      "-"
    );
  };

  const getStoreMeta = (row) => {
    const rowStoreId = row?.store_id ?? row?.STORE_ID ?? row?.store?.store_id ?? row?.storeId ?? row?.id;
    if (rowStoreId !== undefined && rowStoreId !== null) {
      const match = stores.find((store) => Number(store.id) === Number(rowStoreId));
      if (match) return match;
    }

    const name = getStoreName(row);
    if (!name || !stores.length) return null;
    const matches = stores.filter(
      (store) => store?.name?.trim().toLowerCase() === String(name).trim().toLowerCase()
    );
    if (matches.length <= 1) return matches[0] || null;

    const rowDivision = (row?.division || row?.store_division || row?.storeDivision || "").trim();
    if (rowDivision) {
      const divisionMatch = matches.find(
        (store) => store?.division?.trim().toLowerCase() === rowDivision.toLowerCase()
      );
      if (divisionMatch) return divisionMatch;
    }

    return matches[0] || null;
  };

  const normalizeDivision = (divisionValue) => {
    const division = (divisionValue || "").trim();
    if (!division) return "";
    if (usedDivisions.includes(division)) return "Used";
    if (division.toLowerCase() === "rec") return "Rec";
    if (division.toLowerCase() === "auto") return "Auto";
    return division;
  };

  const getMarketingRowDivision = (row) => {
    const division = (row?.division || row?.Division || row?.store_division || row?.storeDivision || "").trim();
    if (division) return normalizeDivision(division);
    const storeMeta = getStoreMeta(row);
    return normalizeDivision(storeMeta?.division || "");
  };

  const getRowDivision = (row) => {
    const directDivision = (row?.division || row?.store_division || row?.storeDivision || "").trim();
    if (directDivision) return normalizeDivision(directDivision);
    const storeMeta = getStoreMeta(row);
    return normalizeDivision(storeMeta?.division || "");
  };

  const getRowDivisionLabel = (row) => {
    const directDivision = (row?.division || row?.store_division || row?.storeDivision || "").trim();
    if (directDivision) return directDivision;
    const storeMeta = getStoreMeta(row);
    return (storeMeta?.division || "").trim();
  };

  const getDisplayStoreName = (row) => {
    const name = getStoreName(row);
    const divisionLabel = getRowDivisionLabel(row);
    if (usedDivisions.includes(divisionLabel)) {
      return `${divisionLabel} - ${name}`;
    }
    return name;
  };

  const getTotalsForRows = (items) => {
    const totalsAcc = items.reduce(
      (acc, row) => {
        acc.visitors += Number(getMetricValue(row, ["visitors"]) || 0);
        acc.sessions += Number(getMetricValue(row, ["sessions"]) || 0);
        acc.newUsers += Number(getMetricValue(row, ["new_users", "newUsers"]) || 0);
        acc.vdpViews += Number(getMetricValue(row, ["vdp_views", "vdpViews"]) || 0);
        return acc;
      },
      { visitors: 0, sessions: 0, newUsers: 0, vdpViews: 0 }
    );

    const avgBounceRate = getAverageMetric(items, ["bounce_rate", "bounceRate"]);
    const avgSessionDuration = getAverageMetric(items, [
      "avg_session_duration_seconds",
      "avg_session_duration",
      "avg_session_duration_sec",
      "avg_session_duration_secs",
      "avg_session_duration_in_seconds",
      "average_session_duration",
      "average_session_duration_seconds",
      "session_duration"
    ]);

    return { ...totalsAcc, avgBounceRate, avgSessionDuration };
  };

  const getVdpTotalsForRows = (items) => {
    const totals = items.reduce(
      (acc, row) => {
        acc.srp_events += Number(row?.srp_events || 0);
        acc.srp_users += Number(row?.srp_users || 0);
        acc.used_vdp_events += Number(row?.used_vdp_events || 0);
        acc.used_vdp_users += Number(row?.used_vdp_users || 0);
        acc.new_vdp_events += Number(row?.new_vdp_events || 0);
        acc.new_vdp_users += Number(row?.new_vdp_users || 0);
        acc.other_vdp_events += Number(row?.other_vdp_events || 0);
        acc.other_vdp_users += Number(row?.other_vdp_users || 0);
        return acc;
      },
      {
        srp_events: 0,
        srp_users: 0,
        used_vdp_events: 0,
        used_vdp_users: 0,
        new_vdp_events: 0,
        new_vdp_users: 0,
        other_vdp_events: 0,
        other_vdp_users: 0
      }
    );

    return {
      ...totals,
      totalVdpEvents: totals.used_vdp_events + totals.new_vdp_events + totals.other_vdp_events,
      totalVdpUsers: totals.used_vdp_users + totals.new_vdp_users + totals.other_vdp_users
    };
  };

  const getChannelName = (row) => {
    return row?.channel || row?.traffic_channel || row?.channel_name || "-";
  };

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const getGroupedAcquisitionData = (items) => {
    const channelMap = {};

    items.forEach((row) => {
      const channelName = getChannelName(row);
      const sessions = toNumber(getMetricValue(row, ["sessions"]));
      const activeUsers = toNumber(getMetricValue(row, ["active_users", "activeUsers"]));
      const newUsers = toNumber(getMetricValue(row, ["new_users", "newUsers"]));

      if (!channelMap[channelName]) {
        channelMap[channelName] = { channel: channelName, sessions: 0, active_users: 0, new_users: 0 };
      }
      channelMap[channelName].sessions += sessions;
      channelMap[channelName].active_users += activeUsers;
      channelMap[channelName].new_users += newUsers;
    });

    const channelSummary = Object.values(channelMap).sort((a, b) => b.sessions - a.sessions);
    const grandTotal = channelSummary.reduce(
      (acc, item) => {
        acc.sessions += item.sessions;
        acc.active_users += item.active_users;
        acc.new_users += item.new_users;
        return acc;
      },
      { sessions: 0, active_users: 0, new_users: 0 }
    );

    return { channelSummary, grandTotal };
  };

  const getUsedGroupKey = (row) => {
    const divisionLabel = (getRowDivisionLabel(row) || "").trim().toLowerCase();
    if (divisionLabel.includes("drivenation")) return "drivenation";
    if (divisionLabel.includes("ffun cars")) return "ffun cars";
    const name = String(getStoreName(row) || "").trim().toLowerCase();
    if (name.includes("drivenation") || name.includes("drive nation")) return "drivenation";
    if (name.includes("ffun cars")) return "ffun cars";
    return name;
  };

  const dedupeUsedRows = (items) => {
    const map = new Map();
    items.forEach((row) => {
      const key = getUsedGroupKey(row);
      if (!map.has(key)) {
        map.set(key, row);
      }
    });
    return Array.from(map.values());
  };

  const buildBarData = (channelSummary, totalSessions) => {
    const pieColors = Object.values(PIE_COLORS);
    const maxBars = 8;
    const topChannels = channelSummary.slice(0, maxBars);
    const otherChannels = channelSummary.slice(maxBars);
    const otherTotal = otherChannels.reduce((sum, item) => sum + item.sessions, 0);
    const barChannels = otherTotal > 0
      ? [...topChannels, { channel: "Other", sessions: otherTotal }]
      : topChannels;

    const minLabelPercent = 0;

    return {
      data: {
        labels: barChannels.map((item) => item.channel),
        datasets: [
          {
            data: barChannels.map((item) => item.sessions),
            backgroundColor: barChannels.map((_, index) => pieColors[index % pieColors.length]),
            borderWidth: 0
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        indexAxis: "y",
        layout: { padding: { top: 8, bottom: 8, left: 8, right: 24 } },
        scales: {
          x: {
            ticks: {
              callback: (value) => Number(value).toLocaleString()
            }
          },
          y: {
            ticks: {
              autoSkip: false
            }
          }
        },
        plugins: {
          legend: { display: false },
          datalabels: {
            color: "#111111",
            backgroundColor: "rgba(255,255,255,0.9)",
            borderRadius: 4,
            padding: 4,
            anchor: "end",
            align: "end",
            offset: 4,
            font: { weight: "bold", size: 10 },
            formatter: (value, context) => {
              const label = context.chart.data.labels?.[context.dataIndex] || "";
              const total = totalSessions || 0;
              const percent = total ? Math.round((value / total) * 100) : 0;
              if (percent < minLabelPercent) return "";
              return label ? `${percent}% (${value})` : `${percent}% (${value})`;
            },
            clamp: true,
            clip: false
          }
        }
      }
    };
  };

  const autoRows = useMemo(() => rows.filter((row) => getRowDivision(row) === "Auto"), [rows, stores]);
  const usedRows = useMemo(
    () => dedupeUsedRows(rows.filter((row) => getRowDivision(row) === "Used")),
    [rows, stores]
  );
  const recRows = useMemo(() => rows.filter((row) => getRowDivision(row) === "Rec"), [rows, stores]);

  const autoVdpRows = useMemo(() => vdpRows.filter((row) => getRowDivision(row) === "Auto"), [vdpRows, stores]);
  const usedVdpRows = useMemo(
    () => dedupeUsedRows(vdpRows.filter((row) => getRowDivision(row) === "Used")),
    [vdpRows, stores]
  );
  const recVdpRows = useMemo(() => vdpRows.filter((row) => getRowDivision(row) === "Rec"), [vdpRows, stores]);

  const autoAcqRows = useMemo(() => acquisitionRows.filter((row) => getRowDivision(row) === "Auto"), [acquisitionRows, stores]);
  const usedAcqRows = useMemo(
    () => dedupeUsedRows(acquisitionRows.filter((row) => getRowDivision(row) === "Used")),
    [acquisitionRows, stores]
  );
  const recAcqRows = useMemo(() => acquisitionRows.filter((row) => getRowDivision(row) === "Rec"), [acquisitionRows, stores]);

  const sections = [
    { key: "Auto", title: "Auto Division", rows: autoRows },
    { key: "Used", title: "Used Division", rows: usedRows },
    { key: "Rec", title: "Rec Division", rows: recRows }
  ];

  const divisionTotals = {
    Auto: getTotalsForRows(autoRows),
    Used: getTotalsForRows(usedRows),
    Rec: getTotalsForRows(recRows)
  };

  const divisionVdpTotals = {
    Auto: getVdpTotalsForRows(autoVdpRows),
    Used: getVdpTotalsForRows(usedVdpRows),
    Rec: getVdpTotalsForRows(recVdpRows)
  };

  const adsTotalsByDivision = useMemo(() => {
    const init = () => ({ impressions: 0, clicks: 0, spend: 0, leads: 0, conversions: 0, conversionValue: 0 });
    const totals = {
      Auto: { Google: init(), Meta: init() },
      Used: { Google: init(), Meta: init() },
      Rec: { Google: init(), Meta: init() }
    };

    const getAdsAccountKey = (row, division) => {
      if (division === "Used") {
        const usedLabel = String(
          row?.division ||
          row?.Division ||
          row?.DIVISION ||
          row?.store?.division ||
          row?.store?.Division ||
          ""
        ).trim().toLowerCase();
        if (usedLabel.includes("drivenation")) return "used:drivenation";
        if (usedLabel.includes("ffun cars")) return "used:ffun cars";
      }
      const account = String(row?.ACCOUNT_NAME || row?.account_name || "").trim().toLowerCase();
      if (account) return `account:${account}`;
      const storeId = row?.store_id ?? row?.STORE_ID ?? row?.store?.store_id ?? row?.storeId ?? row?.id;
      if (storeId !== undefined && storeId !== null) return `store:${storeId}`;
      const storeName = String(getStoreName(row) || "").trim().toLowerCase();
      return storeName ? `store:${storeName}` : "";
    };

    const getAdsDivision = (row) => {
      const direct = (row?.division || row?.Division || row?.DIVISION || row?.store?.division || row?.store?.Division || row?.store_division || row?.storeDivision || "").trim();
      if (direct) return normalizeDivision(direct);
      const meta = getStoreMeta(row);
      return normalizeDivision(meta?.division || "");
    };

    const usedSeen = new Set();
    adsRows.forEach((row) => {
      const division = getAdsDivision(row);
      if (!totals[division]) return;
      const sourceKey = row.__source || "Google";
      if (division === "Used") {
        const key = getAdsAccountKey(row, division);
        const dedupeKey = key ? `${key}|${sourceKey}` : "";
        if (dedupeKey && usedSeen.has(dedupeKey)) return;
        if (dedupeKey) usedSeen.add(dedupeKey);
      }
      if (!totals[division][sourceKey]) return;
      totals[division][sourceKey].impressions += getAdsMetric(row, ["IMPRESSIONS", "impressions", "impression"]);
      totals[division][sourceKey].clicks += getAdsMetric(row, ["CLICKS", "clicks", "click"]);
      totals[division][sourceKey].spend += getAdsMetric(row, ["SPEND", "spend", "cost", "amount_spent"]);
      totals[division][sourceKey].leads += getAdsMetric(row, ["LEADS", "leads", "lead"]);
      totals[division][sourceKey].conversions += getAdsMetric(row, ["CONVERSIONS", "conversions", "conversion"]);
      totals[division][sourceKey].conversionValue += getAdsMetric(row, ["CONVERSIONS_VALUE", "conversions_value"]);
    });

    const calcRates = (totalsRow) => ({
      ctr: totalsRow.impressions ? totalsRow.clicks / totalsRow.impressions : 0,
      cpc: totalsRow.clicks ? totalsRow.spend / totalsRow.clicks : 0,
      cpl: totalsRow.leads ? totalsRow.spend / totalsRow.leads : 0,
      cpa: totalsRow.conversions ? totalsRow.spend / totalsRow.conversions : 0
    });

    const buildDivisionTotals = (division) => {
      const google = totals[division].Google;
      const meta = totals[division].Meta;
      const combined = {
        impressions: google.impressions + meta.impressions,
        clicks: google.clicks + meta.clicks,
        spend: google.spend + meta.spend,
        leads: google.leads + meta.leads,
        conversions: google.conversions + meta.conversions,
        conversionValue: google.conversionValue + meta.conversionValue
      };
      return {
        google,
        meta,
        combined,
        googleRates: calcRates(google),
        metaRates: calcRates(meta),
        combinedRates: calcRates(combined)
      };
    };

    return {
      Auto: buildDivisionTotals("Auto"),
      Used: buildDivisionTotals("Used"),
      Rec: buildDivisionTotals("Rec")
    };
  }, [adsRows, stores]);

  const adsGrandTotals = useMemo(() => {
    const init = () => ({ impressions: 0, clicks: 0, spend: 0, leads: 0, conversions: 0, conversionValue: 0 });
    const divisions = ["Auto", "Rec", "Used"];
    const sumTotals = (key) =>
      divisions.reduce(
        (acc, division) => {
          const data = adsTotalsByDivision[division]?.[key] || init();
          acc.impressions += data.impressions;
          acc.clicks += data.clicks;
          acc.spend += data.spend;
          acc.leads += data.leads;
          acc.conversions += data.conversions;
          acc.conversionValue += data.conversionValue;
          return acc;
        },
        init()
      );

    const google = sumTotals("google");
    const meta = sumTotals("meta");
    const combined = sumTotals("combined");

    const calcRates = (totalsRow) => ({
      ctr: totalsRow.impressions ? totalsRow.clicks / totalsRow.impressions : 0,
      cpc: totalsRow.clicks ? totalsRow.spend / totalsRow.clicks : 0,
      cpl: totalsRow.leads ? totalsRow.spend / totalsRow.leads : 0,
      cpa: totalsRow.conversions ? totalsRow.spend / totalsRow.conversions : 0
    });

    return {
      google,
      meta,
      combined,
      googleRates: calcRates(google),
      metaRates: calcRates(meta),
      combinedRates: calcRates(combined)
    };
  }, [adsTotalsByDivision]);

  const fetchAll = () => {
    setLoading(true);
    setError("");

    const payload = {
      month: month,
      year: Number(year)
    };

    const startDate = moment(`${year}-${month}-01`, "YYYY-MMM-DD").startOf("month");
    const isCurrentMonth = year === moment().format("YYYY") && month === moment().format("MMM");
    const endDate = isCurrentMonth
      ? moment()
      : moment(`${year}-${month}-01`, "YYYY-MMM-DD").endOf("month");
    const adsPayload = {
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD")
    };

    const currentWeek = Math.ceil(moment().date() / 7) || 1;
    const fiscalYear = "FY" + getFiscals(month, year).toString().substring(2);
    const marketingRequests = ["auto", "used", "rec"].map((division) =>
      axios.post(Links.ceoMarketingBreakdown, {
        division,
        month,
        year,
        week: currentWeek,
        fiscal_year: fiscalYear
      })
    );

    const requests = [
      { key: "mtd", promise: axios.post(Links.ga4Mtd, payload) },
      { key: "vdp", promise: axios.post(Links.ga4Vdp, payload) },
      { key: "acquisition", promise: axios.post(Links.ga4Acquisition, payload) },
      { key: "googleAds", promise: axios.post(Links.googleAdsReport, adsPayload) },
      { key: "metaAds", promise: axios.post(Links.metaAdsReport, adsPayload) },
      { key: "marketing", promise: Promise.allSettled(marketingRequests) }
    ];

    Promise.allSettled(requests.map((item) => item.promise))
      .then((results) => {
        const resultMap = results.reduce((acc, result, index) => {
          acc[requests[index].key] = result;
          return acc;
        }, {});

        const getDataRows = (result) =>
          result?.status === "fulfilled" ? result.value?.data?.data || [] : [];

        setRows(getDataRows(resultMap.mtd));
        setVdpRows(getDataRows(resultMap.vdp));
        setAcquisitionRows(getDataRows(resultMap.acquisition));

        const googleRows = getDataRows(resultMap.googleAds).map((row) => ({ ...row, __source: "Google" }));
        const metaRows = getDataRows(resultMap.metaAds).map((row) => ({ ...row, __source: "Meta" }));
        setAdsRows([...googleRows, ...metaRows]);

        const marketingResults = resultMap.marketing?.status === "fulfilled" ? resultMap.marketing.value : [];
        const storeData = marketingResults
          .filter((res) => res.status === "fulfilled")
          .flatMap((res) => res.value?.data?.storeData || []);

        const emptyMetrics = () => ({ fup: { actual: 0, target: 0 }, internet: { actual: 0, target: 0 }, opportunities: { actual: 0, target: 0 } });
        const divisionLeadTotals = { Auto: emptyMetrics(), Used: emptyMetrics(), Rec: emptyMetrics() };

        const usedLeadSeen = new Set();
        storeData.forEach((row) => {
          const division = getMarketingRowDivision(row);
          if (!divisionLeadTotals[division]) return;
          if (division === "Used") {
            const key = getUsedGroupKey(row);
            if (usedLeadSeen.has(key)) return;
            usedLeadSeen.add(key);
          }
          const fup = getMetric(row, "fup", "fup");
          const internet = getMetric(row, "internet", "internet");
          const opportunities = getMetric(row, "opportunities", "opportunities");
          divisionLeadTotals[division].fup.actual += fup.actual;
          divisionLeadTotals[division].fup.target += fup.target;
          divisionLeadTotals[division].internet.actual += internet.actual;
          divisionLeadTotals[division].internet.target += internet.target;
          divisionLeadTotals[division].opportunities.actual += opportunities.actual;
          divisionLeadTotals[division].opportunities.target += opportunities.target;
        });

        setLeadsByDivision(divisionLeadTotals);

        const coreFailed = ["mtd", "vdp", "acquisition"].some(
          (key) => resultMap[key]?.status === "rejected"
        );
        if (coreFailed) {
          setError("Failed to load division level marketing data.");
        } else {
          setError("");
        }
      })
      .catch((err) => {
        console.log("Division level monthly report fetch error:", err);
        setRows([]);
        setVdpRows([]);
        setAcquisitionRows([]);
        setAdsRows([]);
        setLeadsByDivision({
          Auto: { fup: { actual: 0, target: 0 }, internet: { actual: 0, target: 0 }, opportunities: { actual: 0, target: 0 } },
          Used: { fup: { actual: 0, target: 0 }, internet: { actual: 0, target: 0 }, opportunities: { actual: 0, target: 0 } },
          Rec: { fup: { actual: 0, target: 0 }, internet: { actual: 0, target: 0 }, opportunities: { actual: 0, target: 0 } }
        });
        setError("Failed to load division level marketing data.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    axios
      .get(Links.getAllStores)
      .then((res) => {
        setStores(res.data?.stores || []);
      })
      .catch((err) => {
        console.log("Stores fetch error:", err);
        setStores([]);
      });
  }, []);

  useEffect(() => {
    fetchAll();
  }, []);

  return (
    <div style={{ padding: "24px" }}>
      <h2>Division Level - Monthly Report</h2>

      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth>
            <InputLabel id="division-level-month-label">Month</InputLabel>
            <Select
              labelId="division-level-month-label"
              value={month}
              label="Month"
              onChange={(event) => setMonth(event.target.value)}
            >
              {Object.values(MONTHS).map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth>
            <InputLabel id="division-level-year-label">Year</InputLabel>
            <Select
              labelId="division-level-year-label"
              value={year}
              label="Year"
              onChange={(event) => setYear(event.target.value)}
            >
              {YEARS.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={fetchAll}
            disabled={loading}
          >
            {loading ? "Loading..." : "Load"}
          </Button>
        </Grid>
      </Grid>

      {error && (
        <div style={{ color: "#b00020", marginBottom: 16 }}>
          {error}
        </div>
      )}

      <h3 style={{ marginBottom: 12 }}>Leads Summary</h3>
      <div className="table-responsive" style={{ marginBottom: 24 }}>
        <table className="table table-striped table-bordered">
          <thead>
            <tr style={{ background: "#1f2a44", color: "#ffffff" }}>
              <th>Division</th>
              <th colSpan={3} style={{ textAlign: "center" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <PhoneIcon fontSize="small" sx={{ color: "#1976d2" }} />
                  F/Up &amp; P Up
                </span>
              </th>
              <th colSpan={3} style={{ textAlign: "center" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <LanguageIcon fontSize="small" sx={{ color: "#2e7d32" }} />
                  Internet Leads
                </span>
              </th>
              <th colSpan={3} style={{ textAlign: "center" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <InsightsIcon fontSize="small" sx={{ color: "#6d4c41" }} />
                  Total Leads
                </span>
              </th>
              <th>Cost Per Lead</th>
            </tr>
            <tr style={{ background: "#1f2a44", color: "#ffffff" }}>
              <th></th>
              <th>Actual</th>
              <th>Target</th>
              <th style={{ background: "#263656" }}>% of Goal</th>
              <th>Actual</th>
              <th>Target</th>
              <th style={{ background: "#263656" }}>% of Goal</th>
              <th>Actual</th>
              <th>Target</th>
              <th style={{ background: "#263656" }}>% of Goal</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {["Auto", "Rec", "Used"].map((division) => {
              const leads = leadsByDivision[division] || { fup: { actual: 0, target: 0 }, internet: { actual: 0, target: 0 }, opportunities: { actual: 0, target: 0 } };
              const adTotals = adsTotalsByDivision[division]?.combined || { spend: 0 };
              const internetLeads = parseNum(leads.internet.actual);
              const cpl = internetLeads ? adTotals.spend / internetLeads : 0;
              return (
                <tr key={`leads-${division}`}>
                  <td style={{ fontWeight: 600 }}>{division}</td>
                  <td>{formatNumber(leads.fup.actual)}</td>
                  <td>{formatNumber(leads.fup.target)}</td>
                  <td style={{ background: "#fff7e6", fontWeight: 700, color: "#a16207" }}>
                    {calcPercent(leads.fup.actual, leads.fup.target)}
                  </td>
                  <td>{formatNumber(leads.internet.actual)}</td>
                  <td>{formatNumber(leads.internet.target)}</td>
                  <td style={{ background: "#fff7e6", fontWeight: 700, color: "#a16207" }}>
                    {calcPercent(leads.internet.actual, leads.internet.target)}
                  </td>
                  <td>{formatNumber(leads.opportunities.actual)}</td>
                  <td>{formatNumber(leads.opportunities.target)}</td>
                  <td style={{ background: "#fff7e6", fontWeight: 700, color: "#a16207" }}>
                    {calcPercent(leads.opportunities.actual, leads.opportunities.target)}
                  </td>
                  <td>{formatCurrency(cpl)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3 style={{ marginBottom: 12 }}>Division Summary</h3>
      <div className="table-responsive" style={{ marginBottom: 24 }}>
        <table className="table table-striped table-bordered">
          <thead>
            <tr style={{ background: "#1f2a44", color: "#ffffff" }}>
              <th>Division</th>
              <th>Visitors</th>
              <th>Sessions</th>
              <th>New Users</th>
              <th>VDP Views</th>
              <th>Bounce Rate</th>
              <th>Avg Session Duration (s)</th>
            </tr>
          </thead>
          <tbody>
            {["Auto", "Rec", "Used"].map((division) => (
              <tr key={division}>
                <td style={{ fontWeight: 600 }}>{division}</td>
                <td>{formatNumber(divisionTotals[division]?.visitors)}</td>
                <td>{formatNumber(divisionTotals[division]?.sessions)}</td>
                <td>{formatNumber(divisionTotals[division]?.newUsers)}</td>
                <td>{formatNumber(divisionTotals[division]?.vdpViews)}</td>
                <td>{formatPercent(divisionTotals[division]?.avgBounceRate)}</td>
                <td>{formatSeconds(divisionTotals[division]?.avgSessionDuration)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ marginBottom: 12 }}>Ads Spend</h3>
      <div className="table-responsive" style={{ marginBottom: 24 }}>
        <table className="table table-striped table-bordered">
          <thead>
            <tr style={{ background: "#1f2a44", color: "#ffffff" }}>
              <th>Division</th>
              <th>Source</th>
              <th>Spend</th>
              <th>Impressions</th>
              <th>Clicks</th>
              <th>CTR</th>
              <th>CPC</th>
              <th>Leads</th>
              <th>CPL</th>
              <th>Conversions</th>
              <th>CPA</th>
              <th>Conversion Value</th>
            </tr>
          </thead>
          <tbody>
            {["Auto", "Rec", "Used"].map((division) => {
              const totals = adsTotalsByDivision[division] || {
                google: { impressions: 0, clicks: 0, spend: 0, leads: 0, conversions: 0, conversionValue: 0 },
                meta: { impressions: 0, clicks: 0, spend: 0, leads: 0, conversions: 0, conversionValue: 0 },
                combined: { impressions: 0, clicks: 0, spend: 0, leads: 0, conversions: 0, conversionValue: 0 },
                googleRates: { ctr: 0, cpc: 0, cpl: 0, cpa: 0 },
                metaRates: { ctr: 0, cpc: 0, cpl: 0, cpa: 0 },
                combinedRates: { ctr: 0, cpc: 0, cpl: 0, cpa: 0 }
              };

              const rows = [
                { key: "Google", totals: totals.google, rates: totals.googleRates },
                { key: "Meta", totals: totals.meta, rates: totals.metaRates },
                { key: "Total", totals: totals.combined, rates: totals.combinedRates }
              ];

              return rows.map((row, idx) => (
                <tr key={`ads-${division}-${row.key}`} style={row.key === "Total" ? { fontWeight: 600, background: "#f3f4f6" } : undefined}>
                  <td style={{ fontWeight: idx === 0 ? 600 : 400 }}>{idx === 0 ? division : ""}</td>
                  <td>{row.key}</td>
                  <td>{formatCurrency(row.totals.spend)}</td>
                  <td>{formatNumber(row.totals.impressions)}</td>
                  <td>{formatNumber(row.totals.clicks)}</td>
                  <td>{formatPercent(row.rates.ctr)}</td>
                  <td>{formatCurrency(row.rates.cpc)}</td>
                  <td>{formatNumber(row.totals.leads)}</td>
                  <td>{formatCurrency(row.rates.cpl)}</td>
                  <td>{formatNumber(row.totals.conversions)}</td>
                  <td>{formatCurrency(row.rates.cpa)}</td>
                  <td>{formatCurrency(row.totals.conversionValue)}</td>
                </tr>
              ));
            })}
            {["Google", "Meta", "Total"].map((sourceKey, idx) => {
              const totals = sourceKey === "Total"
                ? adsGrandTotals.combined
                : sourceKey === "Google"
                  ? adsGrandTotals.google
                  : adsGrandTotals.meta;
              const rates = sourceKey === "Total"
                ? adsGrandTotals.combinedRates
                : sourceKey === "Google"
                  ? adsGrandTotals.googleRates
                  : adsGrandTotals.metaRates;
              return (
                <tr key={`ads-all-${sourceKey}`} style={sourceKey === "Total" ? { fontWeight: 700, background: "#e8eef7" } : { fontWeight: 600 }}>
                  <td>{idx === 0 ? "All Divisions" : ""}</td>
                  <td>{sourceKey === "Total" ? "Total" : sourceKey}</td>
                  <td>{formatCurrency(totals.spend)}</td>
                  <td>{formatNumber(totals.impressions)}</td>
                  <td>{formatNumber(totals.clicks)}</td>
                  <td>{formatPercent(rates.ctr)}</td>
                  <td>{formatCurrency(rates.cpc)}</td>
                  <td>{formatNumber(totals.leads)}</td>
                  <td>{formatCurrency(rates.cpl)}</td>
                  <td>{formatNumber(totals.conversions)}</td>
                  <td>{formatCurrency(rates.cpa)}</td>
                  <td>{formatCurrency(totals.conversionValue)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3 style={{ marginBottom: 12 }}>Acquisition Summary</h3>
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        {["Auto", "Rec", "Used"].map((division) => {
          const acqRows = division === "Auto" ? autoAcqRows : division === "Used" ? usedAcqRows : recAcqRows;
          const { channelSummary, grandTotal } = getGroupedAcquisitionData(acqRows);
          const barConfig = buildBarData(channelSummary, grandTotal.sessions);
          return (
            <Grid item xs={12} md={4} key={division}>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>{division}</div>
              <div style={{ height: 320 }}>
                <BarChart
                  title=""
                  data={barConfig.data}
                  options={barConfig.options}
                />
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: "#6c757d" }}>
                Total Sessions: {formatNumber(grandTotal.sessions)}
              </div>
            </Grid>
          );
        })}
      </Grid>

      <h3 style={{ marginBottom: 12 }}>VDP &amp; SRP Metrics</h3>
      <div className="table-responsive" style={{ marginBottom: 24 }}>
        <table className="table table-striped table-bordered">
          <thead>
            <tr style={{ background: "#1f2a44", color: "#ffffff" }}>
              <th>Division</th>
              <th>Total VDP Events</th>
              <th>Total VDP Users</th>
              <th>SRP Events</th>
              <th>SRP Users</th>
              <th>Used VDP Events</th>
              <th>Used VDP Users</th>
              <th>New VDP Events</th>
              <th>New VDP Users</th>
              <th>Other VDP Events</th>
              <th>Other VDP Users</th>
            </tr>
          </thead>
          <tbody>
            {["Auto", "Rec", "Used"].map((division) => (
              <tr key={`vdp-${division}`}>
                <td style={{ fontWeight: 600 }}>{division}</td>
                <td>{formatNumber(divisionVdpTotals[division]?.totalVdpEvents)}</td>
                <td>{formatNumber(divisionVdpTotals[division]?.totalVdpUsers)}</td>
                <td>{formatNumber(divisionVdpTotals[division]?.srp_events)}</td>
                <td>{formatNumber(divisionVdpTotals[division]?.srp_users)}</td>
                <td>{formatNumber(divisionVdpTotals[division]?.used_vdp_events)}</td>
                <td>{formatNumber(divisionVdpTotals[division]?.used_vdp_users)}</td>
                <td>{formatNumber(divisionVdpTotals[division]?.new_vdp_events)}</td>
                <td>{formatNumber(divisionVdpTotals[division]?.new_vdp_users)}</td>
                <td>{formatNumber(divisionVdpTotals[division]?.other_vdp_events)}</td>
                <td>{formatNumber(divisionVdpTotals[division]?.other_vdp_users)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sections.map((section) => {
        const sectionTotals = getTotalsForRows(section.rows);
        return (
          <div key={section.key} style={{ marginBottom: 32 }}>
            <h3 style={{ marginBottom: 12 }}>{section.title}</h3>

            <Grid container spacing={2} style={{ marginBottom: 24 }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <div className="card p-2" style={{ borderTop: "4px solid #3f51b5", background: "#f7f8ff" }}>
                  <div className="text-muted">Visitors</div>
                  <div className="h5 mb-0">{formatNumber(sectionTotals.visitors)}</div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <div className="card p-2" style={{ borderTop: "4px solid #00796b", background: "#f2fbf9" }}>
                  <div className="text-muted">Sessions</div>
                  <div className="h5 mb-0">{formatNumber(sectionTotals.sessions)}</div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <div className="card p-2" style={{ borderTop: "4px solid #5d4037", background: "#fbf4f2" }}>
                  <div className="text-muted">New Users</div>
                  <div className="h5 mb-0">{formatNumber(sectionTotals.newUsers)}</div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <div className="card p-2" style={{ borderTop: "4px solid #1565c0", background: "#f2f6fb" }}>
                  <div className="text-muted">VDP Views</div>
                  <div className="h5 mb-0">{formatNumber(sectionTotals.vdpViews)}</div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <div className="card p-2" style={{ borderTop: "4px solid #ff8f00", background: "#fff8e1" }}>
                  <div className="text-muted">Bounce Rate</div>
                  <div className="h5 mb-0">{formatPercent(sectionTotals.avgBounceRate)}</div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <div className="card p-2" style={{ borderTop: "4px solid #00838f", background: "#e0f7fa" }}>
                  <div className="text-muted">Session Duration</div>
                  <div className="h5 mb-0">{formatSeconds(sectionTotals.avgSessionDuration)}</div>
                </div>
              </Grid>
            </Grid>

            <div className="table-responsive">
              <table className="table table-striped table-bordered">
                <thead>
                  <tr style={{ background: "#1f2a44", color: "#ffffff" }}>
                    <th>Store</th>
                    <th>Visitors</th>
                    <th>Sessions</th>
                    <th>New Users</th>
                    <th>VDP Views</th>
                    <th>Bounce Rate</th>
                    <th>Avg Session Duration (s)</th>
                  </tr>
                </thead>
                <tbody>
                  {section.rows.length === 0 && !loading ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center" }}>
                        No data found.
                      </td>
                    </tr>
                  ) : (
                    section.rows.map((row, index) => (
                      <tr key={`${section.key}-${row?.store_id || row?.id || index}`}>
                        <td>{getDisplayStoreName(row)}</td>
                        <td>{formatNumber(getMetricValue(row, ["visitors"]))}</td>
                        <td>{formatNumber(getMetricValue(row, ["sessions"]))}</td>
                        <td>{formatNumber(getMetricValue(row, ["new_users", "newUsers"]))}</td>
                        <td>{formatNumber(getMetricValue(row, ["vdp_views", "vdpViews"]))}</td>
                        <td>{formatPercent(getMetricValue(row, ["bounce_rate", "bounceRate"]))}</td>
                        <td>
                          {formatSeconds(
                            getMetricValue(row, [
                              "avg_session_duration_seconds",
                              "avg_session_duration",
                              "avg_session_duration_sec",
                              "avg_session_duration_secs",
                              "avg_session_duration_in_seconds",
                              "average_session_duration",
                              "average_session_duration_seconds",
                              "session_duration"
                            ])
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
